import { useCallback, useEffect, useState } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

export default function useOrganizationAiSettings() {
  const isMounted = useIsMounted();
  const [fieldEngineerPrompt, setFieldEngineerPrompt] = useState('');
  const [workflowPrompt, setWorkflowPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/organization/ai_settings`);
      if (!res.ok) throw new Error('Failed to load settings');
      const data = await res.json();
      setFieldEngineerPrompt(data.field_engineer_ai_system_prompt || '');
      setWorkflowPrompt(data.workflow_ai_system_prompt || '');
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.message || 'Failed to load');
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const save = useCallback(
    async (tab) => {
      setSaving(true);
      setError(null);
      setSaveMessage(null);
      try {
        const body =
          tab === 'workflow'
            ? { workflow_ai_system_prompt: workflowPrompt }
            : { field_engineer_ai_system_prompt: fieldEngineerPrompt };

        const res = await fetchWithAuth(`${API_URL}/v1/admin/organization/ai_settings`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const msg = Array.isArray(data.errors) ? data.errors.join(', ') : data.error || 'Save failed';
          throw new Error(msg);
        }
        setFieldEngineerPrompt(data.field_engineer_ai_system_prompt || '');
        setWorkflowPrompt(data.workflow_ai_system_prompt || '');
        setSaveMessage('Saved.');
      } catch (err) {
        if (!isMounted.current) return;
        setError(err.message || 'Save failed');
      } finally {
        if (!isMounted.current) return;
        setSaving(false);
      }
    },
    [fieldEngineerPrompt, workflowPrompt, isMounted]
  );

  return {
    fieldEngineerPrompt,
    setFieldEngineerPrompt,
    workflowPrompt,
    setWorkflowPrompt,
    loading,
    saving,
    error,
    saveMessage,
    save,
    refresh: fetchSettings,
  };
}
